import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  Pagination,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";

const panelSx = {
  background: "linear-gradient(180deg, rgba(11,22,36,0.98) 0%, rgba(8,17,29,0.98) 100%)",
  border: "1px solid rgba(126, 170, 220, 0.14)",
  boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
};

const questionCardSx = {
  ...panelSx,
  height: "100%",
  boxShadow: "0 10px 26px rgba(0,0,0,0.16)",
};

const feedbackTextFieldSx = {
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.72)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#55D68E" },
  "& .MuiInputBase-input": { color: "#EDF4FF" },
  "& .MuiInputBase-input::placeholder": { color: "#6F89A6", opacity: 1 },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(8,19,31,0.72)",
    "& fieldset": { borderColor: "rgba(126,170,220,0.2)" },
    "&:hover fieldset": { borderColor: "rgba(85,214,142,0.52)" },
    "&.Mui-focused fieldset": { borderColor: "#55D68E" },
  },
  "& .MuiFormHelperText-root": { color: "#7890AC" },
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function UserFeedback() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalDocs: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    color: "success",
    message: "",
  });

  const showMessage = useCallback((nextMessage, color = "success") => {
    setNotification({ open: true, color, message: nextMessage });
  }, []);

  const loadFeedback = useCallback(
    async (page = 1) => {
      if (init() !== "success") return;

      try {
        setLoading(true);
        const response = await axios.get(`${Constants.getUrls.getUserFeedback}${id}/feedback`, {
          params: { page, limit: 10 },
        });
        const payload = response?.data?.data || {};
        setUser(payload.user || null);
        setQuestions(payload.questions || []);
        setHistory(payload.feedback_history?.docs || []);
        setPagination(payload.feedback_history || { page: 1, totalPages: 1, totalDocs: 0 });
      } catch (error) {
        showMessage(error?.response?.data?.message || "Unable to load user feedback.", "error");
      } finally {
        setLoading(false);
      }
    },
    [id, showMessage]
  );

  useEffect(() => {
    loadFeedback(1);
  }, [loadFeedback]);

  const setAnswer = (questionKey, value) => {
    setAnswers((current) => ({
      ...current,
      [questionKey]: value === "true",
    }));
  };

  const saveFeedback = async () => {
    const unanswered = questions.find(
      (question) => typeof answers[question.key] !== "boolean"
    );

    if (unanswered) {
      showMessage(`Please answer: ${unanswered.text}`, "warning");
      return;
    }

    if (message.length > 5000) {
      showMessage("The feedback message cannot exceed 5000 characters.", "warning");
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(
        `${Constants.postUrls.createUserFeedback}${id}/feedback`,
        {
          answers: questions.map((question) => ({
            question_key: question.key,
            response: answers[question.key],
          })),
          message,
        }
      );

      setAnswers({});
      setMessage("");
      showMessage(response?.data?.message || "User feedback saved successfully.");
      await loadFeedback(1);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to save user feedback.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ background: "transparent", boxShadow: "none", overflow: "visible" }}>
          <MDBox
            mb={3}
            p={{ xs: 2.25, md: 3 }}
            borderRadius="18px"
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={1.5}
            sx={{
              border: "1px solid rgba(85,214,142,0.18)",
              background:
                "radial-gradient(circle at 92% 12%, rgba(31,157,97,0.2), transparent 32%), linear-gradient(135deg, #10243A 0%, #091522 72%)",
              boxShadow: "0 18px 44px rgba(0,0,0,0.22)",
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <MDBox
                width="46px"
                height="46px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="13px"
                sx={{
                  color: "#FFFFFF",
                  background: "linear-gradient(135deg, #28B76F, #147A49)",
                }}
              >
                <Icon>forum</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h4" color="white">
                  User Marketing Feedback
                </MDTypography>
                <MDTypography variant="button" sx={{ color: "#9DB4CF" }}>
                  Record structured feedback collected directly from the user.
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDButton
              component={Link}
              to={`/user-details/${id}`}
              variant="outlined"
              color="secondary"
              sx={{ color: "#D7E5F5 !important", borderColor: "rgba(157,180,207,.42) !important" }}
            >
              Back to User Details
            </MDButton>
          </MDBox>

          {loading && !user ? (
            <MDBox py={8} display="flex" justifyContent="center">
              <CircularProgress color="info" />
            </MDBox>
          ) : (
            <MDBox p={{ xs: 2, md: 3 }}>
              <Card sx={{ ...panelSx, mb: 3 }}>
                <MDBox
                  p={2.5}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ borderLeft: "4px solid #28B76F" }}
                >
                  <MDBox>
                    <MDTypography variant="h5" color="white">
                      {user?.name || "User"}
                    </MDTypography>
                    <MDTypography variant="button" sx={{ color: "#8FA7C4" }}>
                      {user?.mobile || "No mobile"} • {user?.email || "No email"}
                    </MDTypography>
                  </MDBox>
                  <Chip
                    label={`${pagination.totalDocs || 0} saved response${
                      Number(pagination.totalDocs || 0) === 1 ? "" : "s"
                    }`}
                    sx={{
                      color: "#55D68E",
                      backgroundColor: "rgba(40,183,111,0.12)",
                      border: "1px solid rgba(85,214,142,0.34)",
                      fontWeight: 800,
                    }}
                  />
                </MDBox>
              </Card>

              <Card sx={{ ...panelSx, mb: 3, overflow: "hidden" }}>
                <MDBox
                  px={{ xs: 2, md: 3 }}
                  py={2.25}
                  sx={{
                    borderBottom: "1px solid rgba(126,170,220,0.12)",
                    background:
                      "linear-gradient(135deg, rgba(17,35,56,0.96) 0%, rgba(13,29,47,0.9) 100%)",
                  }}
                >
                  <MDTypography variant="h6" sx={{ color: "#EDF4FF" }}>
                    New Questionnaire
                  </MDTypography>
                  <MDTypography variant="button" sx={{ color: "#8FA7C4" }}>
                    Every question is required. Yes is saved as true and No is saved as false.
                  </MDTypography>
                </MDBox>
                <MDBox p={{ xs: 2, md: 3 }}>
                  <Grid container spacing={2}>
                    {questions.map((question, index) => (
                      <Grid item xs={12} md={6} key={question.key}>
                        <Card
                          sx={{
                            ...questionCardSx,
                            border:
                              typeof answers[question.key] === "boolean"
                                ? answers[question.key]
                                  ? "1px solid rgba(85,214,142,0.42)"
                                  : "1px solid rgba(245,164,81,0.42)"
                                : questionCardSx.border,
                            background:
                              typeof answers[question.key] === "boolean"
                                ? answers[question.key]
                                  ? "linear-gradient(145deg, rgba(18,54,45,.72), rgba(8,24,31,.96))"
                                  : "linear-gradient(145deg, rgba(65,40,17,.58), rgba(8,24,31,.96))"
                                : questionCardSx.background,
                          }}
                        >
                          <MDBox p={2.25}>
                            <MDTypography
                              variant="button"
                              color="white"
                              fontWeight="medium"
                              display="block"
                              minHeight={{ md: 48 }}
                            >
                              {index + 1}. {question.text}
                            </MDTypography>
                            <FormControl sx={{ mt: 1 }}>
                              <RadioGroup
                                row
                                value={
                                  typeof answers[question.key] === "boolean"
                                    ? String(answers[question.key])
                                    : ""
                                }
                                onChange={(event) => setAnswer(question.key, event.target.value)}
                              >
                                <FormControlLabel
                                  value="true"
                                  control={
                                    <Radio
                                      sx={{
                                        color: "#54708F",
                                        "&.Mui-checked": { color: "#55D68E" },
                                      }}
                                    />
                                  }
                                  label="Yes"
                                  sx={{ color: "#D7E5F5", mr: 4 }}
                                />
                                <FormControlLabel
                                  value="false"
                                  control={
                                    <Radio
                                      sx={{
                                        color: "#54708F",
                                        "&.Mui-checked": { color: "#F5A451" },
                                      }}
                                    />
                                  }
                                  label="No"
                                  sx={{ color: "#D7E5F5" }}
                                />
                              </RadioGroup>
                            </FormControl>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  <MDBox mt={3}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      label="User message / custom feedback"
                      placeholder="Enter the user's exact feedback, suggestions, complaints, or requested improvements..."
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      helperText={`${message.length}/5000 characters`}
                      inputProps={{ maxLength: 5000 }}
                      sx={feedbackTextFieldSx}
                    />
                  </MDBox>

                  <MDBox mt={2} display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="success"
                      disabled={saving || questions.length === 0}
                      onClick={saveFeedback}
                    >
                      {saving ? "Saving Feedback..." : "Save Feedback"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>

              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <MDBox
                  width="4px"
                  height="24px"
                  borderRadius="4px"
                  sx={{ background: "linear-gradient(180deg, #55D68E, #F27500)" }}
                />
                <MDTypography variant="h6" sx={{ color: "#EDF4FF" }}>
                Feedback History
                </MDTypography>
              </MDBox>

              {history.length === 0 ? (
                <Card sx={panelSx}>
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="button" sx={{ color: "#8FA7C4" }}>
                      No feedback has been recorded for this user yet.
                    </MDTypography>
                  </MDBox>
                </Card>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={2}>
                  {history.map((entry) => (
                    <Card key={entry._id} sx={panelSx}>
                      <MDBox p={{ xs: 2, md: 2.5 }}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                          flexDirection={{ xs: "column", md: "row" }}
                          gap={0.5}
                          mb={2}
                        >
                          <MDTypography variant="button" color="white" fontWeight="medium">
                            Recorded by {entry.recorded_by_name || "Admin"}
                          </MDTypography>
                          <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.62)" }}>
                            {formatDate(entry.createdAt)}
                          </MDTypography>
                        </MDBox>

                        <Grid container spacing={1.25}>
                          {(entry.answers || []).map((answer, index) => (
                            <Grid item xs={12} md={6} key={`${entry._id}-${answer.question_key}`}>
                              <MDBox
                                p={1.5}
                                borderRadius="md"
                                sx={{
                                  backgroundColor: "rgba(255,255,255,0.04)",
                                  border: "1px solid rgba(255,255,255,0.07)",
                                }}
                              >
                                <MDBox display="flex" alignItems="flex-start" gap={1}>
                                  <Chip
                                    size="small"
                                    label={answer.response ? "Yes" : "No"}
                                    sx={{
                                      flexShrink: 0,
                                      color: answer.response ? "#55D68E" : "#F5A451",
                                      backgroundColor: answer.response
                                        ? "rgba(40,183,111,0.12)"
                                        : "rgba(242,117,0,0.12)",
                                      border: answer.response
                                        ? "1px solid rgba(85,214,142,0.34)"
                                        : "1px solid rgba(245,164,81,0.34)",
                                      fontWeight: 800,
                                    }}
                                  />
                                  <MDTypography
                                    variant="caption"
                                    sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.5 }}
                                  >
                                    {index + 1}. {answer.question_text}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </Grid>
                          ))}
                        </Grid>

                        <MDBox
                          mt={2}
                          p={1.75}
                          borderRadius="md"
                          sx={{
                            backgroundColor: "rgba(40,183,111,0.07)",
                            border: "1px solid rgba(85,214,142,0.14)",
                          }}
                        >
                          <MDTypography
                            variant="caption"
                            fontWeight="medium"
                            sx={{ color: "#55D68E" }}
                          >
                            User message
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            display="block"
                            mt={0.5}
                            sx={{
                              color: "rgba(255,255,255,0.82)",
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {entry.message || "No custom message recorded."}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Card>
                  ))}
                </MDBox>
              )}

              {Number(pagination.totalPages || 1) > 1 && (
                <MDBox mt={3} display="flex" justifyContent="center">
                  <Pagination
                    page={Number(pagination.page || 1)}
                    count={Number(pagination.totalPages || 1)}
                    color="info"
                    onChange={(_event, page) => loadFeedback(page)}
                    sx={agriPaginationSx}
                  />
                </MDBox>
              )}
            </MDBox>
          )}

          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
            close={() => setNotification((current) => ({ ...current, open: false }))}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default UserFeedback;
